import type { Projet } from "@mpa/toolbox/models/Projet";

import { SubmitAction } from "../../action/Action";
import { PromiseToast } from "../../components/async/PromiseToast";
import Button from "../../components/Button/Button";
import Form from "../../components/form/Form";
import type { TextFieldProps } from "../../components/form/TextField";
import TextField from "../../components/form/TextField";
import useField from "../../components/form/useField";
import { validationFn } from "../../components/form/utilities";
import OnlyWithPermission from "../../components/OnlyWithPermission";
import LightPageLayout from "../../components/Page/LightPageLayout";
import PageHeader from "../../components/Page/PageHeader";
import Spacer from "../../components/space/Spacer";
import { callOutgoingRequest } from "../../data/tanstack-query";
import { logout, updateProjet } from "../../endpoints/outgoing-requests";
import { Permission } from "../../enums/Permission";
import useSubmit from "../../hooks/useSubmit";
import { navigate } from "../../navigation/location";
import { useCurrentProject } from "../LoggedInNavigator/services/context";
import { indexRoute } from "../RootNavigator/navigation";

const PROJET_LABEL_MAX_LENGTH = 80;

export default function SettingsPage() {
    const [onLogout, logoutSubmission] = useSubmit(async () => {
        await callOutgoingRequest(logout());
        navigate(indexRoute.getLocation());
    }, []);
    return (
        <LightPageLayout>
            <PageHeader title="Réglages" />
            <OnlyWithPermission permission={Permission.Candidate}>
                <ProjetLabelSettings />
            </OnlyWithPermission>
            <PromiseToast promise={logoutSubmission} message="Déconnexion réussie" />
            <Button label="Se déconnecter" action={onLogout} feeling="minor" role="minor" />
        </LightPageLayout>
    );
}

function ProjetLabelSettings() {
    const selectedProjet = useCurrentProject();
    if (!selectedProjet) return null;
    return <ProjetLabelForm key={selectedProjet.id} projet={selectedProjet} />;
}

function ProjetLabelForm(props: { projet: Projet }) {
    const { projet } = props;
    const labelField = useField<TextFieldProps>({
        value: projet.label,
        label: "Libellé",
        required: true,
        validator: validationFn("projet-label", (value) => {
            const trimmed = value.trim();
            if (trimmed.length === 0) throw new Error("Saisissez un libellé");
            if (trimmed.length > PROJET_LABEL_MAX_LENGTH) {
                throw new Error("80 caractères maximum");
            }
        }),
    });
    const [onSave, saving] = useSubmit(async (label: string) => {
        await callOutgoingRequest(updateProjet(label));
    }, []);
    const onSubmit = () => {
        let label: string;
        try {
            label = labelField.validate();
        } catch {
            return;
        }
        onSave(label.trim());
    };
    return (
        <Form onSubmit={onSubmit}>
            <PromiseToast promise={saving} message="Libellé enregistré" />
            <TextField {...labelField.props} />
            <Spacer scale={4} />
            <Button label="Enregistrer le libellé" action={SubmitAction} />
            <Spacer scale={8} />
        </Form>
    );
}
